"use client";
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import StoreInfo from "./StoreInfo";
import SubscriptionTypeClean from "./SubscriptionTypeClean";
import ChatbotSettings from "./ChatbotSetting";
import BrandingAppearance from "./BrandingAppearance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import config from "../../../../config";
import { AuthContext } from "../../../../authentication/AuthContext";
import AcceptJsCardModal from "./AcceptJsCardModal";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

const CreateStore = () => {
  const navigate = useNavigate();
  const { checkSubscriptionStatus } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);
  const CLIENT_ID = config.clientId;

  // Store Info States
  const [storeName, setStoreName] = useState("");
  const [storeTypes, setStoreTypes] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [numLocations, setNumLocations] = useState(1);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [storeUrl, setStoreUrl] = useState("");

  // Subscription States
  const [selectedPlan, setSelectedPlan] = useState("Basic");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [enableFreeTrial, setEnableFreeTrial] = useState(false);
  const [plansData, setPlansData] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(null);

  // Chatbot & Branding States (defaults - will be saved on payment)
  const [chatbotData, setChatbotData] = useState({
    chatbot_name: "Ask Bud-i",
    welcome_message: "Hello! How can I help you today?",
    disclaimer_text: "",
    widget_status: "disabled",
  });
  const [brandingData, setBrandingData] = useState({
    theme_color_hex: "hsl(145 25% 95%)",
    font_style: "Inter",
    chatbot_logo_url: "https://b45dd533.ask-bud-e-cdn.pages.dev/images/Bud-e-logo.webp",
  });

  // UI States
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [storeCreated, setStoreCreated] = useState(false);
  const [createdStoreId, setCreatedStoreId] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Instant session locks
  const isSubmittingRef = useRef(false);
  const createdStoreIdRef = useRef(null);

  // Validation for Store Info
  const isStoreInfoValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      storeName.trim().length >= 3 &&
      contactName.trim().length >= 3 &&
      emailRegex.test(contactEmail.trim()) &&
      storeUrl.trim().length > 0 &&
      storeTypes.length > 0 &&
      zipCodes.length > 0 &&
      numLocations > 0
    );
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch plans data from API
  const fetchPlansData = async () => {
    setLoadingPlans(true);
    try {
      const response = await fetch(`${config.API_BASE_V2_URL}/subscriptions/plans`, {
        headers: config.getHeaders()
      });
      const result = await response.json();

      if (result.success && result.data) {
        // Filter and sort plans in order: Basic, Pro, Premium, Demo
        const order = ["Basic", "Pro", "Premium", "Demo"];
        const sortedPlans = result.data
          .filter(plan => order.includes(plan.plan_name))
          .sort((a, b) => {
            return order.indexOf(a.plan_name) - order.indexOf(b.plan_name);
          });

        setPlansData(sortedPlans);
      } else {
        throw new Error(result.error || "Failed to fetch plans");
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      showSnackbar("Failed to load plans", "error");
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleNext = () => {
    if (activeTab === 0) {
      if (!isStoreInfoValid()) {
        showSnackbar("Please fill all required fields", "error");
        return;
      }
      // Fetch plans data before moving to subscription tab
      fetchPlansData();
      setActiveTab(1);
    }
  };

  const handlePrev = () => {
    if (activeTab > 0) setActiveTab(activeTab - 1);
  };

  const resetWizardState = () => {
    setCreatedStoreId(null);
    createdStoreIdRef.current = null;
    setStoreCreated(false);
    setIsSuccess(false);
    setModalErrorMessage(null);
    setModalIsProcessing(false);
  };

  // Get default values - same for all plans
  const getDefaultChatbotSettings = () => {
    return {
      chatbot_name: "Ask Bud-i",
      welcome_message: "Hello! How can I help you today?",
      disclaimer_text: "",
      widget_status: "disabled",
    };
  };

  const getDefaultBrandingSettings = () => {
    return {
      theme_color_hex: "hsl(145 25% 95%)",
      font_style: "Inter",
      chatbot_logo_url: "https://b45dd533.ask-bud-e-cdn.pages.dev/images/Bud-e-logo.webp",
    };
  };

  // Modal Processing States
  const [modalIsProcessing, setModalIsProcessing] = useState(false);
  const [modalProcessingMessage, setModalProcessingMessage] = useState(null);
  const [modalErrorMessage, setModalErrorMessage] = useState(null);

  const handleCreateStore = async (paymentTokenData = null) => {
    // GUARDRAIL 1: Already successful
    if (isSuccess) return;

    // GUARDRAIL 2: Concurrent submission lock (Instant Ref update)
    if (isSubmittingRef.current) {
      console.warn("⚠️ Submission already in progress. Ignoring duplicate click.");
      return;
    }
    isSubmittingRef.current = true;

    // Final validation
    if (!isStoreInfoValid()) {
      showSnackbar("Please complete Store Info section", "error");
      return;
    }

    if (!paymentTokenData) {
      showSnackbar("Payment information is missing", "error");
      return;
    }

    setLoading(true);
    setModalIsProcessing(true);
    setModalErrorMessage(null);
    setModalProcessingMessage(createdStoreId ? "Resuming activation..." : "Initializing store creation...");
    showSnackbar(createdStoreId ? "Retrying activation..." : "Processing payment and creating store...", "info");

    // Get current plan details
    const currentPlan = plansData?.find((p) => p.plan_name === selectedPlan);

    const payload = {
      client_id: CLIENT_ID,
      store_name: storeName.trim(),
      store_types: storeTypes,
      number_of_locations: numLocations,
      location_zip_codes: zipCodes,
      contact_person_name: contactName.trim(),
      contact_email_id: contactEmail.trim(),
      domain_url: storeUrl.trim(),
      selection: {
        plan_code: currentPlan?.plan_code || selectedPlan,
        trial_opt_in: enableFreeTrial
      },
      payment_token: {
        data_descriptor: paymentTokenData.dataDescriptor,
        data_value: paymentTokenData.dataValue,
      },
      chatbot_defaults: getDefaultChatbotSettings(),
      branding_defaults: getDefaultBrandingSettings(),
    };

    const messages = [
      "Securing your payment details...",
      "Creating your store...",
      "Activating your subscription...",
      "Finalizing setup..."
    ];
    let msgIndex = 0;
    const msgInterval = setInterval(() => {
      msgIndex++;
      if (msgIndex < messages.length) {
        setModalProcessingMessage(messages[msgIndex]);
      }
    }, 3000);

    try {
      setModalProcessingMessage(messages[0]);

      let response;
      let result;

      // Use the Ref for store identity to ensure it's instant across calls
      const activeStoreId = createdStoreIdRef.current || createdStoreId;

      if (activeStoreId) {
        // RETRY / RESUME FLOW
        console.log("🚀 Resuming session for existing store ID:", activeStoreId);
        const resumePayload = {
          client_id: CLIENT_ID,
          payment_token: {
            data_descriptor: paymentTokenData.dataDescriptor,
            data_value: paymentTokenData.dataValue,
          },
          number_of_locations: numLocations,
          plan_code: currentPlan?.plan_code || selectedPlan,
          free_trial: enableFreeTrial
        };

        response = await fetch(`${config.API_BASE_URL}/client/store/${activeStoreId}/activate-pending`, {
          method: "POST",
          headers: config.getHeaders(),
          body: JSON.stringify(resumePayload),
        });
        result = await response.json();
      } else {
        // INITIAL CREATE FLOW
        console.log("🎬 Initiating first-time store creation...");
        response = await fetch(`${config.API_BASE_URL}/client/store/create-with-subscription`, {
          method: "POST",
          headers: config.getHeaders(),
          body: JSON.stringify(payload),
        });
        result = await response.json();
      }

      clearInterval(msgInterval);
      console.log("Create/Resume store response:", result);

      // 1. Instantly capture the identity if returned
      const returnedStoreId = result.data?.client_store_id || result.client_store_id;
      if (returnedStoreId) {
        createdStoreIdRef.current = returnedStoreId;
        setCreatedStoreId(returnedStoreId);
        setStoreCreated(true);
      }

      // 2. Check for soft failures (Identity created but payment failed)
      if (result.error?.code === "STORE_CREATED_ACTIVATION_FAILED" || result.error === "STORE_CREATED_ACTIVATION_FAILED") {
        throw new Error(result.message || "Identity saved, but payment activation failed. Please try again.");
      }

      // 3. Check for hard failures
      if (!response.ok || !result.success) {
        throw new Error(result.error?.details || result.message || "Transaction failed at gateway");
      }

      // 4. Success Path
      if (result.data?.gateway_status === 'subscription_active' || result.gateway_status === 'subscription_active' || result.success) {
        console.log("✅ Activation confirmed.");

        const invUrl = result.data?.invoice_url || result.invoice_url;

        // CRITICAL: Only transition to success if we have the invoice URL
        if (invUrl) {
          console.log("📄 Invoice URL found:", invUrl);
          setInvoiceUrl(invUrl);
          setIsSuccess(true);
          setModalIsProcessing(false);
          setModalProcessingMessage("Success! Store is active.");
        } else {
          console.error("❌ Critical: Succeeded but no invoice URL. Staying in processing to wait...");
          setModalProcessingMessage("Finalizing setup...");
          // We don't transition to Success here anymore.
        }



        setSubscriptionData(result.data || null);
        showSnackbar(result.message || "Store created successfully!", "success");
      }

    } catch (error) {
      console.error("Submission error:", error);
      setModalErrorMessage(error.message || "Failed to process request");
    } finally {
      clearInterval(msgInterval);
      isSubmittingRef.current = false;
      setLoading(false);
      setModalIsProcessing(false);
    }
  };

  const calculateTotalAmount = () => {
    // Get the current plan details from plansData
    const currentPlan = plansData?.find((p) => p.plan_name === selectedPlan);
    if (!currentPlan) return 0;

    const basePrice = parseFloat(currentPlan.plan_amount);
    const totalAmount = basePrice * numLocations;
    const taxPercent = 0; // 0% tax for now
    const tax = totalAmount * (taxPercent / 100);
    return totalAmount + tax;
  };



  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      {/* Progress Banner for Resume Flow */}
      {createdStoreId && (
        <Alert
          severity="success"
          icon={false}
          sx={{
            mb: 3,
            backgroundColor: '#e3f2fd',
            color: '#0d47a1',
            border: '1px solid #bbdefb',
            borderRadius: '8px'
          }}
        >
          ✨ <b>Identity Secured!</b> Your store profile for <b>"{storeName}"</b> is saved. Please complete the activation below to enable your chatbot features.
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "25px",
        }}
      >
        <Typography variant="h3" sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}>
          {createdStoreId ? `Resume Setup: ${storeName}` : "Create Store"}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/store-management")}
        >
          Back
        </Button>
      </Box>

      {/* Tabs - Navigation disabled in create mode */}
      <Tabs value={activeTab} onChange={() => {/* Disabled in create mode */ }}>
        <Tab label={createdStoreId ? "Store Info (Verified)" : "Store Info"} disabled={!!createdStoreId} />
        <Tab label="Subscription & Activation" />
        <Tab label="Chatbot Settings" disabled />
        <Tab label="Branding" disabled sx={{ display: 'none' }} />
      </Tabs>

      {/* Tab Panels */}
      <CustomTabPanel value={activeTab} index={0}>
        <StoreInfo
          storeName={storeName}
          setStoreName={setStoreName}
          storeTypes={storeTypes}
          setStoreTypes={setStoreTypes}
          zipCodes={zipCodes}
          setZipCodes={setZipCodes}
          numLocations={numLocations}
          setNumLocations={setNumLocations}
          contactName={contactName}
          setContactName={setContactName}
          contactEmail={contactEmail}
          setContactEmail={setContactEmail}
          storeUrl={storeUrl}
          setStoreUrl={setStoreUrl}
          isEdit={!!createdStoreId}
        />
      </CustomTabPanel>

      <CustomTabPanel value={activeTab} index={1}>
        <SubscriptionTypeClean
          numLocations={numLocations}
          selectedPlan={selectedPlan}
          setSelectedPlanExternal={(plan) => {
            setSelectedPlan(plan.plan_name);
            setEnableFreeTrial(false); // Reset trial state when plan changes
          }}
          enableFreeTrial={enableFreeTrial}
          setEnableFreeTrial={setEnableFreeTrial}
          plansData={plansData || []}
          isEdit={false}
        />
      </CustomTabPanel>

      <CustomTabPanel value={activeTab} index={2}>
        <ChatbotSettings
          isEditable={true}
          chatbotName={chatbotData.chatbot_name}
          setChatbotName={(v) => setChatbotData({ ...chatbotData, chatbot_name: v })}
          welcomeMessage={chatbotData.welcome_message}
          setWelcomeMessage={(v) => setChatbotData({ ...chatbotData, welcome_message: v })}
          disclaimerText={chatbotData.disclaimer_text}
          setDisclaimerText={(v) => setChatbotData({ ...chatbotData, disclaimer_text: v })}
          logoUrl={brandingData.chatbot_logo_url}
          setLogoUrl={(v) => setBrandingData({ ...brandingData, chatbot_logo_url: v })}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
          ℹ️ These default values will be saved after payment completion.
        </Typography>
      </CustomTabPanel>

      <CustomTabPanel value={activeTab} index={3}>
        <BrandingAppearance
          isEditable={true}
          themeColor={brandingData.theme_color_hex}
          setThemeColor={(v) => setBrandingData({ ...brandingData, theme_color_hex: v })}
          fontStyle={brandingData.font_style}
          setFontStyle={(v) => setBrandingData({ ...brandingData, font_style: v })}
          logoUrl={brandingData.chatbot_logo_url}
          setLogoUrl={(v) => setBrandingData({ ...brandingData, chatbot_logo_url: v })}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
          ℹ️ These default values will be saved after payment completion.
        </Typography>
      </CustomTabPanel>

      {/* Navigation Buttons */}
      <Stack direction="row" alignItems="center" mt={2} spacing={2}>
        <Button variant="outlined" onClick={handlePrev} disabled={activeTab === 0 || loading}>
          Previous
        </Button>
        <Box flex={1} />
        {activeTab === 0 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isStoreInfoValid() || loading || !!createdStoreId}
          >
            {createdStoreId ? "Identity Established" : "Next"}
          </Button>
        )}
        {activeTab === 1 && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowCardModal(true)}
            disabled={loading}
          >
            {createdStoreId ? "Complete Activation" : "Make Payment"}
          </Button>
        )}
      </Stack>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <AcceptJsCardModal
        open={showCardModal}
        onClose={() => {
          if (!modalIsProcessing) {
            if (storeCreated || createdStoreId) {
              console.log("🚀 Redirecting to Store Management after modal closure.");
              navigate("/store-management", { replace: true });
            } else {
              setShowCardModal(false);
              setIsSuccess(false);
            }
          }
        }}
        planSummary={{
          name: selectedPlan,
          price: `$${calculateTotalAmount().toFixed(2)}`,
          description: []
        }}
        enableFreeTrial={enableFreeTrial}
        isProcessing={modalIsProcessing}
        processingMessage={modalProcessingMessage}
        errorMessage={modalErrorMessage}
        successUrl={invoiceUrl}
        isSuccess={isSuccess}
        onSuccessClose={() => navigate("/store-management")}
        onSuccess={(opaqueData) => {
          // Do NOT close modal yet - handleCreateStore will do it after backend success
          handleCreateStore(opaqueData);
        }}
        onError={(message) => {
          // Internal modal handles its own display, but we can sync if needed
        }}
      />

    </Card>
  );
};

export default CreateStore;